import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, CheckCircle2, Info, RotateCcw } from 'lucide-react';
import ordersApi from 'api/requests/orders';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';
import ConfirmDialog from 'modules/dashboard/financial/bank-form/ConfirmDialog';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

const EVENT_ENDED = 'event_ended';

const FALLBACK_ERROR = 'Não foi possível estornar o pedido agora. Tente novamente em instantes.';

/**
 * Estorno do pedido pelo próprio produtor, nas duas telas de detalhe (pedidos
 * gerais e pedidos do evento).
 *
 * Quem decide se dá para estornar é a API (`refund` no detalhe do pedido): aqui
 * só se mostra o resultado. Depois que o evento acaba não há botão, só o
 * motivo. Enquanto ele está ativo o botão fica sempre disponível e o aviso
 * colorido diz se a compra ainda está no prazo do direito do consumidor — verde
 * dentro, vermelho fora —, sem tirar do produtor a escolha de estornar.
 */
export default function OrderRefund({ orderId, refund, onRefunded }) {
    const { can } = usePanelPermissions();
    const [confirming, setConfirming] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!refund) return null;

    if (!refund.can_refund) {
        // Pedido pendente, já estornado ou gratuito: o status do pedido já conta
        // a história. Só o evento encerrado precisa de explicação, porque some um
        // botão que o produtor esperava ver.
        if (refund.blocked_code !== EVENT_ENDED) return null;

        return (
            <Section>
                <EndedNote role='note'>
                    <Info size={16} aria-hidden='true' />
                    <span>{refund.blocked_reason}</span>
                </EndedNote>
            </Section>
        );
    }

    const consumerRight = refund.consumer_right;
    const within = Boolean(consumerRight?.within);
    const canManage = can(PERMISSIONS.ORDERS_MANAGE);

    function openConfirm() {
        setError(null);
        setConfirming(true);
    }

    function closeConfirm() {
        if (loading) return;

        setConfirming(false);
        setError(null);
    }

    async function confirmRefund() {
        setLoading(true);
        setError(null);

        try {
            await ordersApi.producerRefund(orderId);
            setConfirming(false);
            onRefunded?.();
        } catch (err) {
            setError(err?.response?.data?.message || FALLBACK_ERROR);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Section>
            {consumerRight && (
                <Notice $within={within} role='status'>
                    {within
                        ? <CheckCircle2 size={16} aria-hidden='true' />
                        : <AlertTriangle size={16} aria-hidden='true' />}
                    <span>{consumerRight.message}</span>
                </Notice>
            )}

            {canManage && (
                <Actions>
                    <RefundButton type='button' onClick={openConfirm}>
                        <RotateCcw size={15} aria-hidden='true' />
                        Estornar pedido
                    </RefundButton>

                    {!within && (
                        <Hint>Mesmo fora do prazo, você pode estornar se quiser.</Hint>
                    )}
                </Actions>
            )}

            <ConfirmDialog
                visible={confirming}
                title={`Estornar o pedido ${orderId}?`}
                description={
                    'O valor será devolvido ao comprador, descontado do seu saldo, e os ingressos ' +
                    'deste pedido serão cancelados. Essa ação não pode ser desfeita.'
                }
                confirmLabel='Estornar pedido'
                loading={loading}
                error={error}
                onConfirm={confirmRefund}
                onCancel={closeConfirm}
            />
        </Section>
    );
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Notice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid ${({ $within }) => $within ? '#BBF7D0' : '#FECACA'};
    background: ${({ $within }) => $within ? '#DCFCE7' : '#FEE2E2'};
    color: ${({ $within }) => $within ? '#166534' : '#991B1B'};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

const EndedNote = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutral};
    color: ${colors.night};
    font-size: 13px;
    line-height: 1.4;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
`;

const RefundButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 50px;
    border: 1px solid ${colors.danger};
    background: transparent;
    color: ${colors.dangerDark};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.danger}14;
    }

    &:focus-visible {
        outline: 2px solid ${colors.danger};
        outline-offset: 2px;
    }
`;

const Hint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;
